import type NumberUnit from '@exodus/currency';
import { WalletAccount } from '@exodus/models';
import { getChainDisplayName } from '@passkeys/assets';
import Box from '@passkeys/ui/box';
import Button from '@passkeys/ui/button';
import ChainIcon from '@passkeys/ui/chain-icon';
import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import TextField from '@passkeys/ui/text-field';
import { useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import { useParams } from 'wouter';

import SendFee from '@/ui/components/send-fee/index.js';
import SendSummary from '@/ui/components/send-summary';
import SendValidation from '@/ui/components/send-validation/send-validation.js';
import Subpage from '@/ui/components/subpage';
import exodus from '@/ui/exodus';
import useAsset from '@/ui/hooks/use-asset';
import useAvailableBalance from '@/ui/hooks/use-available-balance';
import useExpandedEffect from '@/ui/hooks/use-expanded-effect';
import useFees from '@/ui/hooks/use-fees';
import useIsFeeAssetEnough from '@/ui/hooks/use-is-fee-asset-enough.js';
import useNavigate from '@/ui/hooks/use-navigate';
import useReceiveAddress from '@/ui/hooks/use-receive-address';
import useSendValidation from '@/ui/hooks/use-send-validation.js';
import showSendSuccessModal from '@/ui/modals/send-success-modal';
import { cn } from '@/ui/utils/classnames';
import type { ReferrerLocationState } from '@/ui/utils/navigation';
import { createNavigateStateOptions } from '@/ui/utils/navigation';
import AmountInput from './components/amount-input.js';
import Hero from './components/hero.js';
import RemainingBalance from './components/remaining-balance.js';

function TokenSendPage() {
  const { token } = useParams<{ token: string }>();

  const navigate = useNavigate();

  const walletAccount = WalletAccount.DEFAULT_NAME;

  invariant(token, "TokenReceivePage: Missing 'token' param");

  // TODO: Decide where to trace boundary between legacy terminology ("asset") and new terminology ("token").
  const assetName = token;

  const asset = useAsset(assetName);

  const [amount, setAmount] = useState<NumberUnit | undefined>();

  const [isSendAll, setIsSendAll] = useState(false);

  const [toAddress, setToAddress] = useState('');

  const fiatAmountRef = useRef<NumberUnit | undefined>();
  const modeRef = useRef('crypto');

  const { address: fromAddress } = useReceiveAddress({ assetName });

  const { loading: feesLoading, value: fees } = useFees({
    amount,
    asset,
    fromAddress,
    isSendAll,
    toAddress,
    walletAccount,
  });

  const isFeeAssetEnough = useIsFeeAssetEnough({
    assetName,
    walletAccount,
    fees,
  });

  const availableBalance = useAvailableBalance({
    assetName,
    fees,
    walletAccount,
  });

  if (
    isSendAll &&
    amount &&
    availableBalance &&
    !availableBalance.equals(amount)
  ) {
    setAmount(availableBalance);
  }

  const handleAllClick = () => {
    setIsSendAll(true);
    setAmount(availableBalance);
  };

  const handleAmountChange = ({
    crypto,
    fiat,
  }: {
    crypto: NumberUnit;
    fiat: NumberUnit;
  }) => {
    setIsSendAll(false);
    setAmount(crypto);
    fiatAmountRef.current = fiat;
  };

  const handleModeChange = (mode: string) => {
    modeRef.current = mode;
  };

  const navigateTo = <S extends Record<string, unknown>>(
    route: string,
    state = {} as S,
  ) => {
    navigate(
      route,
      createNavigateStateOptions<S & ReferrerLocationState>({
        ...state,
        referrer: 'token-send',
      }),
    );
  };

  const [sending, setSending] = useState(false);
  const handleSend = async () => {
    if (!asset || !amount) {
      return;
    }

    let success = true;
    let rejected = false;

    try {
      setSending(true);

      const receiver = {
        address: toAddress,
        amount,
      };

      const result = await exodus.transactions.send({
        assetName: asset.name,
        walletAccount: WalletAccount.DEFAULT_NAME,
        receiver,
        isSendAll,
        fees,
      });

      showSendSuccessModal({
        amount,
        asset,
        toAddress,
        txId: result.txId,
      });

      navigate('/tokens');
    } catch (error: any) {
      success = false;
      rejected = error.message?.includes('rejected');

      console.error(error);
    } finally {
      setSending(false);

      exodus.analytics.track({
        event: rejected ? 'WalletAssetSendReject' : 'WalletAssetSend',
        properties: {
          assetName: asset.name,
          network: asset.baseAssetName,
          amount: amount.toDefaultNumber(),
          amountUsd: fiatAmountRef.current?.toDefaultNumber() ?? Number.NaN,
          success: rejected ? undefined : success,
          displayAmountSelection: modeRef.current,
        },
      });
    }
  };

  const {
    error,
    warning,
    action: validationAction,
  } = useSendValidation({
    asset,
    toAddress,
    amount,
    availableBalance,
    fees,
    isFeeAssetEnough,
  });

  const showValidationMessages = error || warning;

  const loading = !asset || !availableBalance;
  const sendLoading = feesLoading || sending;
  const sendDisabled = !amount || !!error || !toAddress;

  useExpandedEffect(() => {
    if (!asset) {
      return;
    }

    exodus.analytics.track({
      event: 'WalletAssetSendView',
      properties: {
        assetName: asset.name,
        network: asset.baseAssetName,
      },
    });
  }, [asset]);

  return (
    <Subpage
      title={
        asset
          ? `Send ${asset.displayTicker} (${getChainDisplayName(asset)})`
          : 'Send'
      }
      className="flex h-full flex-col gap-y-8 pb-8"
    >
      <Hero asset={asset} />

      <Box className="px-4 py-6">
        <Text className="mb-2 leading-none" size={14}>
          Recipient's {asset?.displayTicker} address
        </Text>

        {loading ? (
          <Skeleton height={48} />
        ) : (
          <TextField
            className={cn(
              // TODO: Clean up so that the consumer doesn't have to know about `after`.
              'bg-white after:bg-white theme-dark:bg-neutral-600 theme-dark:after:bg-neutral-600',
              toAddress && 'font-mono',
            )}
            value={toAddress}
            placeholder="Address"
            dataTestId="to-address-input"
            onChange={setToAddress}
            prefix={
              <ChainIcon
                name={asset.baseAssetName}
                displayName={asset.displayName}
              />
            }
          />
        )}

        <Text className="mb-2 mt-6 leading-none" size={14}>
          Amount
        </Text>

        {loading ? (
          <Skeleton height={48} />
        ) : (
          <AmountInput
            asset={asset}
            amount={amount}
            dataTestId="amount-input"
            onAllClick={handleAllClick}
            onAmountChange={handleAmountChange}
            onModeChange={handleModeChange}
          />
        )}

        <Text className="mb-2 mt-6 leading-none" size={14}>
          Summary
        </Text>

        {loading ? (
          <Skeleton height={81} borderRadius={16} />
        ) : (
          <SendSummary>
            <SendFee asset={asset} estimatedFee={fees.fee} monospace />

            <div className="my-2 h-[1px] w-full bg-black/10 theme-dark:bg-white/10" />

            <RemainingBalance
              asset={asset}
              amount={amount}
              availableBalance={availableBalance}
            />
          </SendSummary>
        )}

        <Text
          className="mt-3 leading-none text-gray-400 theme-dark:text-neutral-200"
          size={13}
          align="center"
        >
          All amounts are estimated
        </Text>
      </Box>

      {showValidationMessages && (
        <SendValidation
          error={error}
          warning={warning}
          action={
            validationAction
              ? {
                  label: validationAction.label,
                  onClick: () =>
                    navigateTo(
                      validationAction.navigateTo,
                      validationAction.navigateToState,
                    ),
                }
              : undefined
          }
        />
      )}

      {!sendDisabled && (
        <Button
          className="mx-4"
          size="large"
          disabled={sendDisabled}
          loading={sendLoading}
          onClick={handleSend}
          dataTestId="send-button"
        >
          Send
        </Button>
      )}
    </Subpage>
  );
}

export default TokenSendPage;
